/**
 * API client for user notes endpoints.
 */

import axios from 'axios';
import { getAuthToken, getPortalType } from '@/utils/auth';
import { API_BASE_URL } from './config';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = getAuthToken(getPortalType(window.location.pathname || ''));
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * User Notes API
 */
export const userNotesAPI = {
  /**
   * List all notes for the current user
   * @param {Object} filters - Optional filters (chapter_id, page_number, color, is_pinned)
   * @returns {Promise} Notes list
   */
  list: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.chapter_id) params.append('chapter_id', filters.chapter_id);
      if (filters.page_number) params.append('page_number', filters.page_number);
      if (filters.color) params.append('color', filters.color);
      if (filters.is_pinned !== undefined) params.append('is_pinned', filters.is_pinned);
      if (filters.search) params.append('search', filters.search);
      if (filters.ordering) params.append('ordering', filters.ordering);

      const response = await axios.get(
        `${API_BASE_URL}/users/notes/${params.toString() ? '?' + params.toString() : ''}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  },

  /**
   * Get notes for a specific chapter
   * @param {number} chapterId - Chapter ID
   * @returns {Promise} Notes list for chapter
   */
  getChapterNotes: async (chapterId) => {
    try {
      // Add cache-busting timestamp to prevent service worker/browser cache
      const timestamp = Date.now();
      const response = await axios.get(
        `${API_BASE_URL}/users/notes/?chapter_id=${chapterId}&_t=${timestamp}`,
        { 
          headers: {
            ...getAuthHeaders(),
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      // Handle both paginated and non-paginated responses
      // Paginated: { count, next, previous, results: [...] }
      // Non-paginated: { count, results: [...] }
      // Direct array: [...]
      let notes = [];
      
      console.log('📝 Raw response.data:', response.data);
      console.log('📝 response.data type:', typeof response.data);
      console.log('📝 response.data.results:', response.data?.results);
      console.log('📝 response.data.results type:', typeof response.data?.results);
      console.log('📝 response.data.results isArray:', Array.isArray(response.data?.results));
      
      if (Array.isArray(response.data)) {
        notes = response.data;
        console.log('✅ Using direct array (response.data is array)');
      } else if (response.data && response.data.results !== undefined) {
        // Check if results exists (even if it's an empty array)
        if (Array.isArray(response.data.results)) {
          notes = response.data.results;
          console.log('✅ Using results array, count:', notes.length);
        } else {
          console.warn('⚠️ response.data.results exists but is not an array:', response.data.results);
          notes = [];
        }
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        notes = [];
      }
      
      console.log('📝 API Response structure:', {
        isArray: Array.isArray(response.data),
        hasResults: !!response.data?.results,
        resultsIsArray: Array.isArray(response.data?.results),
        resultsLength: response.data?.results?.length,
        notesCount: notes.length,
        rawDataKeys: response.data ? Object.keys(response.data) : [],
        rawDataCount: response.data?.count,
        rawDataResults: response.data?.results
      });
      
      return notes;
    } catch (error) {
      console.error('Error fetching chapter notes:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  /**
   * Create a new note
   * @param {Object} noteData - Note data
   * @param {number} noteData.chapter_id - Chapter ID
   * @param {string} noteData.heading - Optional heading
   * @param {string} noteData.content - Note content
   * @param {number} noteData.page_number - Page number
   * @param {Object} noteData.position - Position {x, y}
   * @param {string} noteData.selected_text - Optional selected text
   * @param {string} noteData.section_name - Optional section name
   * @param {string} noteData.color - Note color (default: yellow)
   * @param {boolean} noteData.is_pinned - Whether note is pinned
   * @returns {Promise} Created note
   */
  create: async (noteData) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/users/notes/`,
        noteData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating note:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Get a single note by ID
   * @param {number} noteId - Note ID
   * @returns {Promise} Note object
   */
  get: async (noteId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/users/notes/${noteId}/`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching note:', error);
      throw error;
    }
  },

  /**
   * Update a note
   * @param {number} noteId - Note ID
   * @param {Object} updates - Partial note data to update
   * @returns {Promise} Updated note
   */
  update: async (noteId, updates) => {
    try {
      console.log('📝 Updating note:', { noteId, updates });
      const response = await axios.patch(
        `${API_BASE_URL}/users/notes/${noteId}/`,
        updates,
        { headers: getAuthHeaders() }
      );
      console.log('✅ Note updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating note:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Delete a note
   * @param {number} noteId - Note ID
   * @returns {Promise}
   */
  delete: async (noteId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/users/notes/${noteId}/`,
        { headers: getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },
};

/**
 * Get teacher notes for student's division and chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Promise} Array of teacher notes
 */
export const getStudentTeacherNotes = async (chapterId) => {
  console.log(`📚 getStudentTeacherNotes() called with chapterId=${chapterId}`);
  console.log(`🔗 API URL: ${API_BASE_URL}/users/teacher-notes/?chapter_id=${chapterId}`);
  
  try {
    const response = await axios.get(
      `${API_BASE_URL}/users/teacher-notes/?chapter_id=${chapterId}`,
      { headers: getAuthHeaders() }
    );
    
    console.log(`✅ API Response received:`, {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      notesCount: response.data?.notes?.length || 0,
      hasNotes: !!response.data?.notes,
      notesArray: response.data?.notes
    });
    
    // API returns { notes: [...] }
    const notes = response.data.notes || [];
    console.log(`📝 Returning ${notes.length} note(s) from API`);
    
    if (notes.length > 0) {
      notes.forEach((note, index) => {
        console.log(`   Note ${index + 1}: ID=${note.id}, title="${note.title?.substring(0, 50)}..."`);
      });
    } else {
      console.warn(`⚠️ No notes found in response. Response data:`, response.data);
    }
    
    return notes;
  } catch (error) {
    console.error('❌ Error fetching teacher notes:', {
      message: error.message,
      response: error.response,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });
    throw error;
  }
};

